import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAllNotifications, setNotificationsSeen } from '../../../actions';
import { Consts } from '../../../res';
import { checkNotificationLink } from '../../../helpers/checkNotificationLink';
import { markNotificationsRead } from '../services/notifications.service';

/**
 * The notifications the header's bell and the dashboard's panel both read.
 *
 * Redux, deliberately, and for the same reason `auth/` uses it: the list is
 * already in the store, filled at sign-in by `afterStudentSignin` and topped up
 * by the Firebase listener in `NotificationsManager.jsx`. A fetch of its own
 * here would give the bell and the panel two different answers about how many
 * are unread.
 *
 * The store holds two kinds, and this returns both. `NORMAL` is the everyday
 * one. `IMPORTANT` is what the admin panel sends as "IMPORTANT_NOTIFY" — the
 * subscription confirmations are sent that way too. While unread it also opens
 * a dialogue by itself (`useImportantNotifications`); afterwards the bell is
 * where the reader finds it again. The old app kept it behind a red envelope
 * of its own, which the new chrome does not draw.
 *
 * So the unread count is the store's two counts added together.
 */
const KINDS = [Consts.NOTIFICATIONS_TYPES.NORMAL, Consts.NOTIFICATIONS_TYPES.IMPORTANT];

/*
 * Two components call this hook — the header's bell and the dashboard's panel —
 * and on a cold restore both mount with an empty list in the same commit. Their
 * effects then run one after the other, and neither sees the other's `loading`,
 * because both are reading the snapshot from a render that happened first.
 *
 * So the guard cannot live in the store. This is module-level and deliberately
 * not a ref: a ref is per-component, which is exactly the thing that does not
 * help here.
 */
let isFetching = false;

const useNotifications = () => {
    const dispatch = useDispatch();
    const { notifications, notNormalSeen, notImportantSeen, loading } = useSelector((state) => state.notifications);

    // Sign-in fetches the list, so this only fires for a session restored from
    // a cookie, where `checkAuth` runs but the list has never been asked for.
    useEffect(() => {
        if (notifications.length || isFetching) return;

        isFetching = true;
        Promise.resolve(dispatch(getAllNotifications())).finally(() => { isFetching = false; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const items = useMemo(() => {
        return notifications
            .filter((n) => KINDS.includes(n.type))
            // The server returns them in insertion order, which is not always
            // newest first once the push listener has prepended a live one.
            .slice()
            .sort((a, b) => new Date(b.creatAt) - new Date(a.creatAt))
            .map((n) => ({
                ...n,
                isUnread: !n.readAt,
                // `checkNotificationLink` parses `dataJson` without checking it
                // is there. A notification sent without a payload — every
                // hand-written one from the admin panel — makes it throw, and
                // an exception in a map takes the whole dashboard down with it.
                href: safeLink(n),
            }));
    }, [notifications]);

    /**
     * Mark everything on screen as read.
     *
     * This is what the design's "مسح الكل" does. It cannot delete: the API has
     * a read endpoint and no clear endpoint, so nothing here can remove a
     * notification from the list. What it can do is take the unread mark off
     * all of them, which is the part of "clear" the reader can actually see —
     * the count beside the heading and the dot on the bell both go.
     *
     * Optimistic in the store either way: the reducer updates from the ids it
     * is handed, and a failed request leaves the server one round behind until
     * the next load rather than leaving the reader looking at a dead button.
     */
    const [isClearing, setIsClearing] = useState(false);

    const markRead = useCallback(async (ids) => {
        if (!ids.length) return;
        dispatch(setNotificationsSeen(ids));

        try {
            await markNotificationsRead(ids);
        } catch {
            // Swallowed on purpose. The reader asked to dismiss a badge, not to
            // save something; a toast about it would be noise about a number.
        }
    }, [dispatch]);

    const markAllRead = useCallback(async () => {
        const unread = items.filter((n) => n.isUnread).map((n) => n.notificationId);
        if (!unread.length || isClearing) return;

        setIsClearing(true);
        try {
            await markRead(unread);
        } finally {
            setIsClearing(false);
        }
    }, [items, isClearing, markRead]);

    return { items, unreadCount: notNormalSeen + notImportantSeen, loading, markRead, markAllRead };
};

const safeLink = (notification) => {
    if (!notification.dataJson) return '#';
    try {
        return checkNotificationLink(notification);
    } catch {
        return '#';
    }
};

export default useNotifications;
